import { Box, Paper, Typography, Button, useTheme } from "@mui/material";
import { FiPlus, FiRefreshCw, FiCloudLightning, FiDollarSign, FiChevronRight, FiBarChart2, FiShield, FiLayers } from "react-icons/fi";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddButton from "../../components/button/AddButton";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import GenerateReportButton from "../../components/button/GenerateReportButton";
import RefreshButton from "../../components/button/RefreshButton";
import SaveButton from "../../components/button/SaveButton";
import CostPerGWPChart from "../../components/chart/CostPerGWPChart";
import GWPTotalChart from "../../components/chart/GWPTotalChart";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import ResizableBox from "../../components/container/ResizableBox";
import StandardCardContainer from "../../components/container/StandardCardContainer";
import { TableFooterCheckBoxesContainer } from "../../components/container/TableFooterCheckBoxesContainer";
import { TableFooterContainer } from "../../components/container/TableFooterContainer";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import CheckBox from "../../components/input/CheckBox";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import ReportTypeSelect from "../../components/select/ReportTypeSelect";
import MetricCard from "../../app_components/MetricCard";
import OverallPerformanceAnalytics from "./OverallAnalytics";
import OverallIntelligence from "./OverallIntelligence";
function OrganizationCommonDashboard({ organization, colDefs, title, subtitle, tabletitle, pageType, colVisibility_key }) {
  const theme = useTheme();
  const gridHeaderContainerRef = useRef();
  const { t } = useTranslation();
  const { columnVisibilities, refresh, setRefresh } = useContextProvider();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [focusedOrganization, setFocusedOrganization] = useState();
  const gridRef = useRef(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [globalView, setGlobalView] = useState(false);
  const [documentView, setDocumentView] = useState(false);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const selectedOrganization = useMemo(() => {
    return selectedOrganizations.length === 1 ? selectedOrganizations[0] : null;
  }, [selectedOrganizations]);
  const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
  const newOrganization = () => {
    organization
      .new()
      .then((res) => res.json())
      .then((data) => setFocusedOrganization(data))
      .finally(() => setRefresh((r) => !r));
  };
  const updateOrganization = (o) => {
    organization.update(o).finally(() => { });
  };
  const onSelectionChanged = (event) => {
    const selectedOrganizations = event.api.getSelectedRows();
    setSelectedOrganizations(selectedOrganizations);
  };
  const onCellValueChanged = (params) => {
    const { data } = params;
    const updatedRow = data;
    updateOrganization(updatedRow);
    setOrganizations((prev) => prev.map((o) => (o.id === updatedRow.id ? { ...updatedRow } : o)));
  };
  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };
  const handleCloseMenu = () => {
    setMenuVisible(false);
  };
  const duplicateOrganization = (selectedOrganizations) => {
    const ids = selectedOrganizations.map((s) => s.id);
    organization.duplicate(ids).finally(() => {
      setSelectedOrganizations([]);
      setRefresh((r) => !r);
    });
  };
  const deleteOrganization = (selectedOrganizations) => {
    const ids = selectedOrganizations.map((s) => s.id);
    organization.delete(ids).finally(() => {
      setSelectedOrganizations([]);
      setRefresh((r) => !r);
    });
  };
  const columnDefs = useMemo(
    () =>
      colDefs.map((col) => {
        if (col.editable) {
          return { ...col, onCellValueChanged, hide: isHidden(col.field) };
        }
        return { ...col, hide: isHidden(col.field) };
      }),
    [colDefs, columnVisibilities]
  );
  const defaultCols = useMemo(() => columnDefs.map((col) => col.field), [columnDefs]);
  const contextMenuOptions = [
    { label: "Duplicate", action: duplicateOrganization, needSelectedItems: true },
    { label: "Delete", action: deleteOrganization, needSelectedItems: true },
  ];
  useEffect(() => {
    if (!focusedOrganization || !focusedOrganization.id || !gridRef.current) return;
    setTimeout(() => {
      const api = gridRef.current.api;
      api.paginationGoToLastPage();
      setTimeout(() => {
        const node = api.getRowNode(focusedOrganization.id);
        if (node) {
          api.ensureIndexVisible(node.rowIndex, "top");
          node.setSelected(true, true);
          setFocusedOrganization(undefined);
        }
      }, 50);
    }, 50);
  }, [organizations, focusedOrganization]);
  useEffect(() => {
    organization
      .get_mine()
      .then((res) => res.json())
      .then((data) => setOrganizations(data));
  }, [refresh]);
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);
  // Aggregated Metrics
  const totalGWP = useMemo(() => organizations.reduce((sum, o) => sum + (Number(o.gwp) || 0), 0), [organizations]);
  const totalCost = useMemo(() => organizations.reduce((sum, o) => sum + (Number(o.cost) || 0), 0), [organizations]);

  return (
    <MainContainer>
      {/* ── Premium Header Band ────────────────────────────────── */}


      {/* ── Enterprise KPI Grid ────────────────────────────────── */}
      <Box className="org-stats-grid" sx={{ mb: 4 }}>
        <MetricCard
          title={t("Total Entities")}
          value={organizations.length}
          icon={FiLayers}
          type="chain"
          accent="#0284c7"
          bg={theme.palette.mode === 'dark' ? 'rgba(2, 132, 199, 0.15)' : 'rgba(14, 165, 233, 0.1)'}
          delay={0.1}
        />

        <MetricCard
          title={t("Aggregate Footprint")}
          value={totalGWP}
          unit="kg CO₂e"
          icon={FiCloudLightning}
          type="gwp"
          accent="#ea580c"
          bg={theme.palette.mode === 'dark' ? 'rgba(234, 88, 12, 0.15)' : 'rgba(249, 115, 22, 0.1)'}
          delay={0.2}
        />

        <MetricCard
          title={t("Economic Impact")}
          value={totalCost}
          unit="$"
          icon={FiDollarSign}
          type="cost"
          accent="#16a34a"
          bg={theme.palette.mode === 'dark' ? 'rgba(22, 163, 74, 0.15)' : 'rgba(34, 197, 94, 0.1)'}
          delay={0.3}
        />

        <MetricCard
          title={t("Data Reliability")}
          value="100%"
          icon={FiShield}
          type="shield"
          accent="#8b5cf6"
          bg={theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(168, 85, 247, 0.1)'}
          delay={0.4}
        />
      </Box>

      {/* ── Centered Tabbed Navigation ─────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <Box className="org-tab-container">
          <Box
            className={`org-tab-item ${!globalView && !documentView ? "active" : ""}`}
            onClick={() => { setGlobalView(false); setDocumentView(false); }}
          >
            <FiLayers size={14} />
            Data Directory
          </Box>
          <Box
            className={`org-tab-item ${globalView ? "active" : ""}`}
            onClick={() => { setGlobalView(true); setDocumentView(false); }}
          >
            <FiBarChart2 size={14} />
            Performance Analytics
          </Box>
          <Box
            className={`org-tab-item ${documentView ? "active" : ""}`}
            onClick={() => { setDocumentView(true); setGlobalView(false); }}
          >
            <FiShield size={14} />
            Intelligence Hub
          </Box>
        </Box>
      </Box>

      {/* ── Dynamic Content Zone ────────────────────────────────── */}
      <Box sx={{ minHeight: "600px", position: "relative" }}>

        {/* VIEW 1: Ag-Grid Directory */}
        {!globalView && !documentView && (
          <Paper variant="Table Container" sx={{ animation: "org-fadeInUp 0.4s ease" }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {t(tabletitle)} ({organizations.length})
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
                <SearchBox text={searchText} setText={setSearchText} />
                <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
                <AddButton onClick={newOrganization} />
                <ExportAsXLSXButton data={organizations} label="Export as .xlsx" filename="organizations.xlsx" />
                <RefreshButton />
              </TableHeaderToolsContainer>
            </TableHeaderContainer>
            <Box>
              <ResizableBox>
                <AgGrid ref={gridRef} columnDefs={columnDefs} rowData={organizations} onSelectionChanged={onSelectionChanged} handleContextMenu={handleContextMenu} />
              </ResizableBox>
            </Box>
            <TableFooterContainer>
              {/* Clean Footer */}
            </TableFooterContainer>
          </Paper>
        )}

        {/* VIEW 2: Performance Analytics */}
        {globalView && (
          <OverallPerformanceAnalytics data={organizations} />
        )}

        {/* VIEW 3: Intelligence Hub */}
        {documentView && (
          <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
            <OverallIntelligence data={organizations} />
          </Box>
        )}
      </Box>

      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedOrganizations} options={contextMenuOptions} />
      <SaveButton />
    </MainContainer >
  );
}

export default OrganizationCommonDashboard;
